import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { BASE_URL } from "@/config/api";
import { getAccessToken, getOrganizationId } from "@/utils/token_functions";
import { secureAxios } from "@/middleware/encryptionMiddleware";

export const createPollSurveyAsync = createAsyncThunk(
  "polls/createPollSurvey",
  async (pollData, { rejectWithValue }) => {
    try {
      const token = getAccessToken();

      if (!token) {
        throw new Error("No authentication token found");
      }

      // format the poll data
      const formattedPollData = {
        ...pollData,
        organizationID: getOrganizationId(),
      };

      const response = await secureAxios.post(
        `${BASE_URL}/v1/surveyPolls`,
        formattedPollData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
            Accept: "application/json",
          },
        }
      );

      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  },
  {
    meta: {
      requiresNetwork: true,
    },
  }
);

export const fetchPollSurveysAsync = createAsyncThunk(
  "polls/fetchPollSurveys",
  async ({ skip, limit }, { rejectWithValue }) => {
    try {
      const token = getAccessToken();
      const organizationID = getOrganizationId();

      const response = await secureAxios.get(`${BASE_URL}/v1/surveyPolls`, {
        params: {
          skip,
          limit,
          organizationID,
        },
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      return {
        polls: response.data.data,
        total: response.data.data.length,
      };
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
  {
    meta: {
      requiresNetwork: true,
    },
  }
);

export const fetchPollByAny = createAsyncThunk(
  "polls/fetchPollByAny",
  async ({ key, value }, { rejectWithValue }) => {
    try {
      const token = getAccessToken();
      const response = await secureAxios.get(
        `${BASE_URL}/v1/surveyPolls/by-any/${key}/${value}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      return {
        data: response.data.data,
        total: response.data.data.length, // Include the total count
        isSinglePoll: key === "_id", // Flag to indicate if we're fetching by ID
      };
    } catch (error) {
      // If it's a 404 error (not found), return an empty array instead of rejecting
      if (error.response?.status === 404) {
        return { data: [], total: 0, isSinglePoll: false };
      }
      return rejectWithValue(error.response?.data || error.message);
    }
  },
  {
    meta: {
      requiresNetwork: true,
    },
  }
);

export const updatePollSurvey = createAsyncThunk(
  "polls/updatePollSurvey",
  async (pollData, { rejectWithValue }) => {
    try {
      const token = getAccessToken();
      const response = await secureAxios.patch(
        `${BASE_URL}/v1/surveyPolls`,
        pollData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
  {
    meta: {
      requiresNetwork: true,
    },
  }
);

// Submit a respondent's answer to a poll
export const respondToPollAsync = createAsyncThunk(
  "polls/respondToPoll",
  async ({ pollId, body }, { rejectWithValue }) => {
    try {
      const token = getAccessToken();
      const response = await secureAxios.post(
        `${BASE_URL}/v1/surveyPolls/${pollId}/answer`,
        body,
        {
          headers: {
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
            "Content-Type": "application/json",
          },
        }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
  {
    meta: {
      requiresNetwork: true,
    },
  }
);

export const deletePollSurvey = createAsyncThunk(
  "polls/deletePollSurvey",
  async (pollId, { rejectWithValue }) => {
    try {
      const token = getAccessToken();
      const response = await secureAxios.delete(`${BASE_URL}/v1/surveyPolls`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        data: [pollId],
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
  {
    meta: {
      requiresNetwork: true,
    },
  }
);

const initialState = {
  isLoading: false,
  error: null,
  success: false,
  polls: [],
  fetchedPolls: [],
  currentPoll: null,
  total: 0, // Add total for pagination
  isOfflineData: false,
  lastFetchTime: null, // Add lastFetchTime for cache freshness tracking
};

const pollsSlice = createSlice({
  name: "polls",
  initialState,
  reducers: {
    clearPollState: (state) => {
      state.error = null;
      state.success = false;
      state.currentPoll = null;
    },
    loadCachedData: (state, action) => {
      return {
        ...state,
        ...action.payload,
        isOfflineData: true,
      };
    },
    setOfflineMode: (state, action) => {
      state.isOfflineData = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(createPollSurveyAsync.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(createPollSurveyAsync.fulfilled, (state, action) => {
        state.isLoading = false;
        state.success = true;
        state.polls.push(action.payload);
        state.currentPoll = action.payload;
      })
      .addCase(createPollSurveyAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
        state.success = false;
      })
      .addCase(fetchPollSurveysAsync.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchPollSurveysAsync.fulfilled, (state, action) => {
        state.isLoading = false;
        state.polls = action.payload.polls;
        state.total = action.payload.total;
        state.error = null;
        state.lastFetchTime = new Date().toISOString();
      })
      .addCase(fetchPollSurveysAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(fetchPollByAny.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchPollByAny.fulfilled, (state, action) => {
        state.isLoading = false;

        // The API now returns items shaped as { poll, respondentCount, respondents, results }.
        const normalizedData = action.payload.data.map((item) => ({
          ...(item.poll ?? item), // support both old flat shape and new nested shape
          respondentCount: item.respondentCount ?? 0,
          respondents: item.respondents ?? [],
          results: item.results ?? [],
        }));

        state.fetchedPolls = normalizedData;
        state.total = action.payload.total;

        if (action.payload.isSinglePoll && normalizedData.length > 0) {
          state.currentPoll = normalizedData[0];
        } else {
          state.currentPoll = normalizedData.length > 0 ? normalizedData[0] : null;
        }
        state.error = null;
        state.lastFetchTime = new Date().toISOString();
      })
      .addCase(fetchPollByAny.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(updatePollSurvey.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updatePollSurvey.fulfilled, (state) => {
        state.isLoading = false;
        state.success = true;
        state.error = null;
      })
      .addCase(updatePollSurvey.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(deletePollSurvey.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(deletePollSurvey.fulfilled, (state) => {
        state.isLoading = false;
        state.success = true;
        state.error = null;
      })
      .addCase(deletePollSurvey.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(respondToPollAsync.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(respondToPollAsync.fulfilled, (state) => {
        state.isLoading = false;
        state.success = true;
        state.error = null;
      })
      .addCase(respondToPollAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });
  },
});

export const { clearPollState, loadCachedData, setOfflineMode } =
  pollsSlice.actions;

// Selectors
export const selectPollsLoading = (state) => state.polls.isLoading;
export const selectPollsError = (state) => state.polls.error;
export const selectPollsSuccess = (state) => state.polls.success;
export const selectAllPolls = (state) => state.polls.polls;
export const selectFetchedPolls = (state) => state.polls.fetchedPolls || [];
export const selectCurrentPoll = (state) => state.polls.currentPoll;
export const selectPollTotal = (state) => state.polls.total;
export const selectPollsByField = (state) => {
  const poll = state.polls.currentPoll;
  return poll ? [poll] : [];
};

export const selectIsOfflineData = (state) =>
  state.polls.isOfflineData || false;

export default pollsSlice.reducer;
